import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  type ReactNode,
  type RefObject,
  type SyntheticEvent,
} from 'react';
import { Crop, Pause, Play, Repeat, Volume2, VolumeX, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { autoDetectCrop } from '@/features/converter/convertActions';
import { useProjectStore } from '@/store/projectStore';
import { clamp, formatTimecode } from '@/utils/format';
import { cn } from '@/utils/cn';
import { CropOverlay } from './CropOverlay';

interface ContentRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

function ToolbarButton({
  label,
  active,
  disabled,
  loading,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  disabled?: boolean;
  loading?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      aria-label={label}
      title={label}
      aria-pressed={active}
      disabled={disabled}
      loading={loading}
      onClick={onClick}
      className={cn('px-2', active && 'bg-signal-500/15 text-signal-600 dark:text-signal-400')}
    >
      {children}
    </Button>
  );
}

export function VideoStage({ videoRef }: { videoRef: RefObject<HTMLVideoElement | null> }) {
  const sourceUrl = useProjectStore((state) => state.sourceUrl);
  const meta = useProjectStore((state) => state.meta);
  const trim = useProjectStore((state) => state.trim);
  const crop = useProjectStore((state) => state.crop);
  const setCrop = useProjectStore((state) => state.setCrop);
  const previewLoop = useProjectStore((state) => state.previewLoop);
  const setPreviewLoop = useProjectStore((state) => state.setPreviewLoop);
  const speed = useProjectStore((state) => state.settings.speed);
  const phase = useProjectStore((state) => state.conversion.phase);
  const converting = phase === 'loading-engine' || phase === 'palette' || phase === 'encoding';

  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [timeText, setTimeText] = useState('0:00.0');
  const [detecting, setDetecting] = useState(false);
  const [stageEl, setStageEl] = useState<HTMLDivElement | null>(null);
  const [contentRect, setContentRect] = useState<ContentRect | null>(null);

  // Where the letterboxed video content actually sits inside the stage —
  // the coordinate frame the crop overlay maps into.
  useLayoutEffect(() => {
    if (!stageEl || !meta) return;
    const update = () => {
      const box = stageEl.getBoundingClientRect();
      const scale = Math.min(box.width / meta.width, box.height / meta.height);
      const width = meta.width * scale;
      const height = meta.height * scale;
      setContentRect({
        left: (box.width - width) / 2,
        top: (box.height - height) / 2,
        width,
        height,
      });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(stageEl);
    return () => observer.disconnect();
  }, [stageEl, meta]);

  // Preview at the speed the GIF will play.
  useEffect(() => {
    const video = videoRef.current;
    if (video) video.playbackRate = clamp(speed, 0.25, 4);
  }, [speed, videoRef, sourceUrl]);

  const onTimeUpdate = useCallback(
    (event: SyntheticEvent<HTMLVideoElement>) => {
      const video = event.currentTarget;
      setTimeText(formatTimecode(video.currentTime));
      if (previewLoop && video.currentTime >= trim.end - 0.03) {
        video.currentTime = trim.start;
        if (!video.paused) void video.play().catch(() => undefined);
      }
    },
    [previewLoop, trim.end, trim.start],
  );

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      if (previewLoop && (video.currentTime < trim.start || video.currentTime >= trim.end - 0.05)) {
        video.currentTime = trim.start;
      }
      void video.play().catch(() => undefined);
    } else {
      video.pause();
    }
  }, [previewLoop, trim.end, trim.start, videoRef]);

  const toggleCrop = useCallback(() => {
    if (!meta) return;
    if (crop) {
      setCrop(null);
      return;
    }
    const width = Math.round(meta.width * 0.8);
    const height = Math.round(meta.height * 0.8);
    setCrop({
      x: Math.round((meta.width - width) / 2),
      y: Math.round((meta.height - height) / 2),
      width,
      height,
    });
  }, [crop, meta, setCrop]);

  const runAutoCrop = useCallback(async () => {
    setDetecting(true);
    try {
      await autoDetectCrop();
    } finally {
      setDetecting(false);
    }
  }, []);

  if (!sourceUrl || !meta) return null;

  return (
    <div className="space-y-2">
      <div
        ref={setStageEl}
        className="relative overflow-hidden rounded-xl bg-black ring-1 ring-ink-200 dark:ring-ink-700"
      >
        <video
          ref={videoRef}
          src={sourceUrl}
          muted={muted}
          playsInline
          preload="metadata"
          onTimeUpdate={onTimeUpdate}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          className="block max-h-[56vh] w-full object-contain"
        />
        {crop && contentRect && (
          <CropOverlay
            contentRect={contentRect}
            source={{ width: meta.width, height: meta.height }}
            value={crop}
            onChange={setCrop}
            onRemove={() => setCrop(null)}
          />
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1">
        <ToolbarButton label={playing ? 'Pause' : 'Play'} onClick={togglePlay}>
          {playing ? (
            <Pause aria-hidden className="size-4" />
          ) : (
            <Play aria-hidden className="size-4" />
          )}
        </ToolbarButton>
        <ToolbarButton
          label={muted ? 'Unmute preview' : 'Mute preview'}
          onClick={() => setMuted((m) => !m)}
        >
          {muted ? (
            <VolumeX aria-hidden className="size-4" />
          ) : (
            <Volume2 aria-hidden className="size-4" />
          )}
        </ToolbarButton>
        <ToolbarButton
          label="Loop the trimmed range"
          active={previewLoop}
          onClick={() => setPreviewLoop(!previewLoop)}
        >
          <Repeat aria-hidden className="size-4" />
        </ToolbarButton>

        <span className="mx-1 font-mono text-xs text-ink-500 tabular-nums dark:text-ink-400">
          {timeText} <span className="text-ink-300 dark:text-ink-600">/</span>{' '}
          {formatTimecode(meta.duration)}
        </span>

        <div className="ml-auto flex items-center gap-1">
          <ToolbarButton
            label={crop ? 'Remove crop' : 'Add crop'}
            active={!!crop}
            onClick={toggleCrop}
          >
            <Crop aria-hidden className="size-4" />
            <span className="hidden sm:inline">{crop ? 'Cropped' : 'Crop'}</span>
          </ToolbarButton>
          <ToolbarButton
            label="Detect and crop black bars"
            disabled={converting}
            loading={detecting}
            onClick={() => void runAutoCrop()}
          >
            {!detecting && <Wand2 aria-hidden className="size-4" />}
            <span className="hidden sm:inline">Auto-crop</span>
          </ToolbarButton>
        </div>
      </div>
    </div>
  );
}
